import {MetadataModel, MetadataRepository, BeatAPIMetadataRepository, Tag} from 'ts-core';
import React, {
    createContext,
    useContext,
    ReactNode,
    useCallback,
    useRef,
    useState,
    useEffect,
    useMemo,
} from "react";
import {useApi} from "./Api";

interface BalloonEventsMetadataInfo {
    metadata: MetadataModel;
    visibleTags: Tag[];
    isSystemTag: (slug: string) => boolean;
    loading: boolean;
    refreshMetadata: () => Promise<void>;
}

const EMPTY_METADATA = new MetadataModel([], [], []);

const BalloonEventsMetadataContext = createContext<BalloonEventsMetadataInfo | undefined>(undefined);

type BalloonEventsMetadataProviderProps = {
    children: ReactNode;
    legacyAPI: boolean;
    metadata?: any;
    blockWhileLoading?: boolean;
};

export const BalloonEventsMetadataProvider: React.FC<
    BalloonEventsMetadataProviderProps
> = ({children, legacyAPI, metadata = null, blockWhileLoading = true}) => {
    const {apiEndpoints, apiKey} = useApi();
    const repositoryRef = useRef<MetadataRepository>(
        new BeatAPIMetadataRepository(apiEndpoints.metadata, apiKey, {
            categories: apiEndpoints.categories,
            tags: apiEndpoints.tags,
        })
    );
    const [loading, setLoading] = useState(true);
    const [metadataState, setMetadataState] = useState<MetadataModel | null>(null);

    // Generate cache key based on API hostname to prevent cross-backend cache conflicts
    const getCacheKey = useCallback(() => {
        try {
            const url = new URL(apiEndpoints.metadata);
            return `balloon-events-metadata-${url.hostname}`;
        } catch {
            return 'balloon-events-metadata';
        }
    }, [apiEndpoints.metadata]);

    // update cache
    const loadMetadata = useCallback(async (background = false) => {
        if (!background) {
            setLoading(true);
        }

        repositoryRef.current = new BeatAPIMetadataRepository(apiEndpoints.metadata, apiKey, {
            categories: apiEndpoints.categories,
            tags: apiEndpoints.tags,
        });

        try {
            await repositoryRef.current.loadMetadata();

            if (typeof window !== 'undefined') {
                const cacheKey = getCacheKey();
                window.localStorage.setItem(cacheKey, JSON.stringify(repositoryRef.current.metadata));
            }

            setMetadataState(repositoryRef.current.metadata);
        } catch (error) {
            console.error("context", 'Failed to load metadata from API:', error);
        } finally {
            setLoading(false);
        }
    }, [apiEndpoints.categories, apiEndpoints.metadata, apiEndpoints.tags, apiKey, getCacheKey]);

    const refreshMetadata = useCallback(async () => {
        await loadMetadata(true);
    }, [loadMetadata]);

    const visibleTags = useMemo(
        () => (metadataState ?? EMPTY_METADATA).tags.filter((tag) => tag.type !== 'system'),
        [metadataState]
    );

    const systemTagSlugs = useMemo(
        () => new Set((metadataState ?? EMPTY_METADATA).tags
            .filter((tag) => tag.type === 'system')
            .map((tag) => tag.slug.toLowerCase())),
        [metadataState]
    );

    const isSystemTag = useCallback((slug: string) => {
        return systemTagSlugs.has(slug.toLowerCase());
    }, [systemTagSlugs]);

    useEffect(() => {
        if (metadata) {
            repositoryRef.current.metadata = metadata;
            setMetadataState(repositoryRef.current.metadata);
            setLoading(false);
        } else {
            const cacheKey = getCacheKey();
            if (typeof window !== 'undefined') {
                const cachedItem = window.localStorage.getItem(cacheKey);

                if (cachedItem) {
                    try {
                        const cachedData = JSON.parse(cachedItem);

                        if (MetadataModel.is(cachedData)) {
                            repositoryRef.current.metadata = cachedData;
                            setMetadataState(repositoryRef.current.metadata);
                            setLoading(false);
                            void loadMetadata(true);
                            return;
                        }
                    } catch (error) {
                        console.error("context", 'Failed to parse cached metadata:', error);
                    }
                }
            }

            void loadMetadata();
        }
    }, [getCacheKey, loadMetadata, metadata]);

    return (
        <BalloonEventsMetadataContext.Provider
            value={{
                metadata: metadataState ?? EMPTY_METADATA,
                visibleTags,
                isSystemTag,
                loading,
                refreshMetadata,
            }}
        >
            {(!loading || !blockWhileLoading) && children}
        </BalloonEventsMetadataContext.Provider>
    );
};

export function useBalloonEventsMetadata(): BalloonEventsMetadataInfo {
    const context = useContext(BalloonEventsMetadataContext);
    if (!context) {
        throw new Error(
            "useBalloonEventsMetadata must be used within a BalloonEventsMetadataProvider"
        );
    }
    return context;
}
